/**
 * Mutation « mot de passe oublié » — `Passwords/forgot` (guide API v1.3).
 *
 * Endpoint PUBLIC → `mutatePublicData` (pas de Bearer). Le user-service répond
 * TOUJOURS 202 (anti-énumération) : l'UI affiche le même message générique
 * quelle que soit l'existence du compte.
 */
import { useMutation } from "@tanstack/react-query";

import { mutatePublicData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { QUERY_KEYS } from "@/core/api/query-keys";
import { logger } from "@/core/utils/func/logger";
import { authEndpoints } from "@/features/auth/api/endpoints";
import type { ForgotPasswordFormValues } from "@/features/auth/schemas/forgot-password.schema";

export const useForgotPassword = () => {
  return useMutation({
    mutationKey: QUERY_KEYS.AUTH_FORGOT_PASSWORD(),
    mutationFn: (body: ForgotPasswordFormValues) =>
      // 202 sans corps utile — rien à typer côté réponse.
      mutatePublicData({
        url: authEndpoints.forgotPassword,
        reqBody: body,
        method: "POST",
      }) as Promise<void>,
    onError: (error) => {
      // L'erreur ne doit jamais révéler l'existence du compte : le composant
      // affiche un échec générique uniquement en cas de faute réseau/serveur.
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useForgotPassword failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "auth" }
      );
    },
  });
};
